import { Box, Button, Stack, SvgIcon, Typography } from "@mui/material";
import { useRouter } from "next/router";
import React, { useState } from "react";
import Carousel from "react-material-ui-carousel";
import {
  IPromptsType,
  useCreatePromptNudgeMutation,
} from "../../services/profileAPI";
import AddIcon from "@mui/icons-material/Add";
import PromptCard from "./PromptCard";
import { useAppSelector } from "../../app/hooks";
import { useSnackbar } from "notistack";

function ProfilePrompts({
  prompts,
  isSameUser,
  name,
  userId,
  hasPoked = false,
  pokeCount,
}: {
  prompts: IPromptsType[];
  isSameUser: boolean;
  name: string;
  userId: string;
  hasPoked: boolean;
  pokeCount: number;
}) {
  const router = useRouter();
  const [activeIndex, setActiveIndex] = useState<null | number>(1);
  const [createPromptNudge, { isLoading: isCreatePromptNudgeLoading }] =
    useCreatePromptNudgeMutation();

  const { enqueueSnackbar } = useSnackbar();

  const navigateToPrompts = () => {
    router.push("/profile/answer-prompts");
  };

  const pokeMember = async () => {
    try {
      const response = await createPromptNudge({
        otherUserId: userId,
      }).unwrap();
      if (response) {
        enqueueSnackbar("poke successfully sent", { variant: "success" });
      }
    } catch (error: any) {
      if (error?.data?.message === "already nudged") {
        enqueueSnackbar("you have already poked this member", {
          variant: "error",
        });
      } else {
        enqueueSnackbar(
          "an error occured while poking the member. please try again later",
          {
            variant: "error",
          }
        );
      }
    }
  };

  if (!isSameUser && prompts.length === 0) {
    return (
      <>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="400">
          we have added fun prompts so that you get to know {name} better. poke
          her to fill them up.
        </Typography>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          mt={3}
          mb={1}
        >
          <Button
            variant="outlined"
            color="secondary"
            disabled={isCreatePromptNudgeLoading || hasPoked}
            sx={{
              borderRadius: 5,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
            onClick={pokeMember}
          >
            <Stack direction="row" alignItems="flex-end">
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight="400"
                mr={1}
              >
                {hasPoked ? "poked" : "poke"}
              </Typography>{" "}
              <SvgIcon fontSize="medium">
                <path
                  fill="currentColor"
                  d="M21,9A1,1 0 0,1 22,10A1,1 0 0,1 21,11H16.53L16.4,12.21L14.2,17.15C14,17.65 13.47,18 12.86,18H8.5C7.7,18 7,17.27 7,16.5V10C7,9.61 7.16,9.26 7.43,9L11.63,4.1L12.4,4.84C12.6,5.03 12.72,5.29 12.72,5.58L12.69,5.8L11,9H21M2,18V10H5V18H2Z"
                />
              </SvgIcon>
              {pokeCount > 0 && (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight="400"
                  ml={1}
                >
                  {pokeCount}
                </Typography>
              )}
            </Stack>
          </Button>
        </Stack>
      </>
    );
  }

  return (
    <>
      {prompts?.length! > 0 ? (
        <>
          <Carousel
            navButtonsAlwaysVisible
            indicators={false}
            animation="slide"
            duration={500}
            fullHeightHover={false}
            stopAutoPlayOnHover
            onChange={(now) => setActiveIndex(now! + 1)}
          >
            {prompts?.map((prompt) => (
              <PromptCard
                key={prompt.questionId}
                question={prompt.question}
                answer={prompt.answer}
                questionId={prompt.questionId}
                isSameUser={isSameUser}
              />
            ))}
          </Carousel>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            fontWeight="400"
            gutterBottom
            sx={{ textAlign: "center" }}
            mt={1.5}
          >
            {activeIndex}/{prompts?.length}
          </Typography>
          {isSameUser && (
            <Stack direction="row" justifyContent="center" mb={1}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddIcon fontSize="small" />}
                sx={{
                  mt: 2,
                  borderRadius: "24px",
                }}
                onClick={navigateToPrompts}
              >
                add more prompts
              </Button>
            </Stack>
          )}
        </>
      ) : (
        <Box mb={1}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight="400"
          >
            members would love to know you better and we have added prompts to
            enable that. show us your fun side.
          </Typography>
          {isSameUser && (
            <Stack direction="row" justifyContent="center">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddIcon fontSize="small" />}
                sx={{
                  mt: 2,
                  borderRadius: "24px",
                }}
                onClick={navigateToPrompts}
              >
                answer prompts
              </Button>
            </Stack>
          )}
        </Box>
      )}
    </>
  );
}

export default ProfilePrompts;
